import { useState, useEffect, useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { useDateLocale } from '@/hooks/useDateLocale';
import { Loader2, Calendar, Bell, AlertTriangle, Info, MapPin } from 'lucide-react';
import { format, startOfDay, endOfDay, subDays } from 'date-fns';
import { DateRange } from 'react-day-picker';
import { DashboardLayout } from '@/components/dashboard/DashboardLayout';
import { useTraccarSession } from '@/hooks/useTraccarSession';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  fetchDevices,
  fetchEvents,
  type Device,
  type Event,
} from '@/lib/traccar-api';

const eventTypeIcons: Record<string, typeof Bell> = {
  alarm: AlertTriangle,
  geofenceEnter: MapPin,
  geofenceExit: MapPin,
  deviceOnline: Info,
  deviceOffline: Info,
};

const Events = () => {
  const { t } = useTranslation();
  const dateFnsLocale = useDateLocale();
  const { isAuthenticated } = useTraccarSession();

  const [devices, setDevices] = useState<Device[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: startOfDay(subDays(new Date(), 7)),
    to: endOfDay(new Date()),
  });

  const loadInitialData = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      setIsLoading(true);
      const devicesData = await fetchDevices();

      if (devicesData.length > 0) {
        setDevices(devicesData);
        if (!selectedDeviceId && devicesData.length > 0) {
          setSelectedDeviceId(devicesData[0].id);
        }
      }
      setHasLoaded(true);
    } catch (error) {
      console.error('Failed to load initial data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, selectedDeviceId]);

  const loadEvents = useCallback(async () => {
    if (!selectedDeviceId || !dateRange?.from || !dateRange?.to) return;

    try {
      setIsLoadingEvents(true);
      const from = format(dateRange.from, "yyyy-MM-dd'T'HH:mm:ss'Z'");
      const to = format(dateRange.to, "yyyy-MM-dd'T'HH:mm:ss'Z'");

      const eventsData = await fetchEvents(selectedDeviceId, from, to);
      setEvents(eventsData);
    } catch (error) {
      console.error('Failed to load events:', error);
      setEvents([]);
    } finally {
      setIsLoadingEvents(false);
    }
  }, [selectedDeviceId, dateRange]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (selectedDeviceId && dateRange?.from && dateRange?.to) {
      loadEvents();
    }
  }, [selectedDeviceId, dateRange, loadEvents]);

  if (isLoading && !hasLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const selectedDevice = devices.find(d => d.id === selectedDeviceId);

  const headerContent = (
    <div className="flex items-center gap-4">
      <Select
        value={selectedDeviceId?.toString() ?? ""}
        onValueChange={(value) => setSelectedDeviceId(parseInt(value, 10))}
      >
        <SelectTrigger className="w-[200px]">
          <SelectValue placeholder={t('history.selectDevice')} />
        </SelectTrigger>
        <SelectContent>
          {devices.map((device) => (
            <SelectItem key={device.id} value={device.id.toString()}>
              {device.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="gap-2">
            <Calendar className="h-4 w-4" />
            {dateRange?.from ? (
              dateRange.to ? (
                <>
                  {format(dateRange.from, "MMM d", { locale: dateFnsLocale })} - {format(dateRange.to, "MMM d, yyyy", { locale: dateFnsLocale })}
                </>
              ) : (
                format(dateRange.from, "MMM d, yyyy", { locale: dateFnsLocale })
              )
            ) : (
              t('history.selectDates')
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <CalendarComponent
            mode="range"
            selected={dateRange}
            onSelect={setDateRange}
            numberOfMonths={2}
            disabled={(date) => date > new Date()}
          />
        </PopoverContent>
      </Popover>
    </div>
  );

  return (
    <DashboardLayout headerContent={headerContent}>
      <div className="p-6 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              {t('nav.events')}
              {selectedDevice && (
                <Badge variant="secondary" className="ml-2">
                  {selectedDevice.name}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingEvents ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : events.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Bell className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>{t('notifications.noEvents')}</p>
                <p className="text-sm mt-1">{t('notifications.noEventsHint')}</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                {events.map((event) => {
                  const IconComponent = eventTypeIcons[event.type] || Bell;
                  return (
                    <div
                      key={event.id}
                      className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <IconComponent className="h-5 w-5 text-primary" />
                        <div className="flex-1">
                          <div className="font-medium">{t(`notifications.events.${event.type}`, event.type)}</div>
                          <div className="text-sm text-muted-foreground">
                            {format(new Date(event.eventTime), 'MMM d, yyyy HH:mm:ss', { locale: dateFnsLocale })}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Events;
